import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'design-system';
import { ChartNoAxesColumnIncreasing, ChartSpline } from 'lucide-react';

import { DEFAULT_SEASONAL_YEARS } from '@/features/dashboard/components/AppForm/AppForm/components/SelectSeasonalRange';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import { AppType } from '@/features/dashboard/types';

const ChartTypeLabel = ({
  icon,
  type,
  info
}: {
  icon: React.ReactNode;
  type: string;
  info: string;
}) => {
  return (
    <span className='flex items-center gap-2.5'>
      {icon}
      <span className='text-sm'>{type}</span>
      <span className='text-xs font-medium text-muted-foreground'>{info}</span>
    </span>
  );
};

const items = [
  {
    value: AppType.Spot,
    label: (
      <ChartTypeLabel
        icon={<ChartNoAxesColumnIncreasing />}
        info='Up to 6 items'
        type='Spot History chart'
      />
    )
  },
  {
    value: AppType.Seasonal,
    label: (
      <ChartTypeLabel
        icon={<ChartSpline />}
        info='1 item only'
        type='Seasonal chart'
      />
    )
  }
] as const;

type ChartTypeFieldValue = (typeof items)[number]['value'];

export const SelectChartType = () => {
  const { form } = useAppForm();

  const handleTypeChange = (
    nextType: ChartTypeFieldValue,
    handleChange: (value: ChartTypeFieldValue) => void
  ) => {
    handleChange(nextType);
    const values = form.state.values;
    const hasName = !!values && 'name' in values;
    const currentName = hasName ? values.name : undefined;
    if (nextType === AppType.Seasonal) {
      if (!form.getFieldValue('from')) {
        form.setFieldValue('from', DEFAULT_SEASONAL_YEARS);
      }
      if (hasName && currentName === 'Spot History chart') {
        form.setFieldValue('name', 'Seasonal chart');
      }
    } else if (nextType === AppType.Spot && hasName && currentName === 'Seasonal chart') {
      form.setFieldValue('name', 'Spot History chart');
    }
  };

  return (
    <form.Field
      children={field => (
        <section className='flex w-full flex-col gap-2 rounded-xl bg-card p-4'>
          <h3 className='text-sm font-semibold'>Chart type</h3>
          <p className='mb-2 text-xs font-medium text-muted-foreground'>
            Select the type of chart you want to create
          </p>
          <Select
            value={field.state.value}
            onValueChange={value => {
              handleTypeChange(value as ChartTypeFieldValue, field.handleChange);
            }}
          >
            <SelectTrigger
              aria-label='Chart type'
              className='w-full'
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {items.map(item => (
                <SelectItem
                  key={item.value}
                  value={item.value}
                >
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </section>
      )}
      name='type'
    />
  );
};
